'use client';

import { Moon, Sun } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useThemeStore } from '@/core/stores/theme.store';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface ThemeToggleProps {
  id?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// ThemeToggle — compact topbar button to switch between dark and light themes
// ---------------------------------------------------------------------------
export function ThemeToggle({ id, className }: ThemeToggleProps) {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  const isDark = theme === 'dark';

  return (
    <button
      id={id || "theme-toggle-btn"}
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className={cn(
        'relative flex items-center justify-center w-10 h-10 rounded-lg',
        'text-hms-textSub hover:text-hms-text',
        'hover:bg-white/[0.05]',
        'transition-all duration-200',
        className
      )}
    >
      {/* Sun icon — visible in dark mode (click to go light) */}
      <Sun
        id="theme-toggle-sun-icon"
        size={18}
        strokeWidth={1.8}
        className={cn(
          'absolute transition-all duration-300',
          isDark
            ? 'opacity-100 scale-100 rotate-0'
            : 'opacity-0 scale-75 rotate-90'
        )}
      />

      {/* Moon icon — visible in light mode (click to go dark) */}
      <Moon
        id="theme-toggle-moon-icon"
        size={18}
        strokeWidth={1.8}
        className={cn(
          'absolute transition-all duration-300',
          isDark
            ? 'opacity-0 scale-75 -rotate-90'
            : 'opacity-100 scale-100 rotate-0'
        )}
      />
    </button>
  );
}
